import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Select,
  MenuItem,
  FormControl,
  Stack,
} from '@mui/material';
import { normalizeTimestamp, formatValue } from './chartUtils';
import WidgetState from './WidgetState';

const TimeSeriesTable = ({ data, config = {} }) => {
  const [sortedBy, setSortedBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [displayLimit, setDisplayLimit] = useState(10);

  // Memoize data processing
  const tableData = useMemo(() => {
    if (!data || !data.data || data.type !== 'timeseries') {
      return null;
    }
    return processTimeseriesForTable(data.data);
  }, [data]);

  // Memoize columns
  const columns = useMemo(() => {
    if (!tableData) return [];
    return [
      { key: 'timestamp', label: 'Timestamp' },
      ...tableData.metrics.map((metric, index) => ({
        key: `value_${index}`,
        label: metric.name,
      })),
    ];
  }, [tableData]);

  // Memoize sorted + limited data
  const displayData = useMemo(() => {
    if (!tableData) return [];

    const sorted = [...tableData.rows].sort((a, b) => {
      if (sortedBy === 'timestamp') {
        return sortOrder === 'asc'
          ? a.timestampRaw - b.timestampRaw
          : b.timestampRaw - a.timestampRaw;
      }
      const aVal = a[sortedBy] ?? 0;
      const bVal = b[sortedBy] ?? 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted.slice(0, displayLimit);
  }, [tableData, sortedBy, sortOrder, displayLimit]);

  // Updaters passed to setState must be PURE. Calling setSortOrder from inside
  // the setSortedBy updater meant StrictMode's double-invocation toggled the
  // direction twice, so clicking a sorted column appeared to do nothing in
  // development. Compute from the current values instead.
  const handleSort = useCallback((columnKey) => {
    if (sortedBy === columnKey) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortedBy(columnKey);
      setSortOrder('desc');
    }
  }, [sortedBy]);

  const handleLimitChange = useCallback((event) => {
    setDisplayLimit(event.target.value);
  }, []);

  if (!tableData || tableData.rows.length === 0) {
    return (
      <WidgetState isEmpty />
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Controls */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">Show:</Typography>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Select value={displayLimit} onChange={handleLimitChange} size="small">
              <MenuItem value={10}>10 rows</MenuItem>
              <MenuItem value={25}>25 rows</MenuItem>
              <MenuItem value={50}>50 rows</MenuItem>
              <MenuItem value={100}>100 rows</MenuItem>
              <MenuItem value={1000}>All</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {/* Table */}
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sortDirection={sortedBy === col.key ? sortOrder : false}
                  sx={{ fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                >
                  <TableSortLabel
                    active={sortedBy === col.key}
                    direction={sortedBy === col.key ? sortOrder : 'desc'}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayData.map((row) => (
              <TableRow key={row.key} hover>
                <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                  {row.timestamp}
                </TableCell>
                {tableData.metrics.map((_, index) => (
                  <TableCell key={index} sx={{ fontSize: '0.8rem' }}>
                    {typeof row[`value_${index}`] === 'number'
                      ? formatValue(row[`value_${index}`], config)
                      : 'N/A'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer */}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Showing {displayData.length} of {tableData.rows.length} rows
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {tableData.metrics.length} metrics
        </Typography>
      </Stack>
    </Box>
  );
};

// Process timeseries data for table display
// Optimized: uses Map for O(1) lookups instead of Array.find O(n)
function processTimeseriesForTable(timeseriesData) {
  // Normalize to epoch ms BEFORE dedup so seconds/millis mixes collapse to one
  // key and both the sort and the per-row lookups agree on the key.
  const timestamps = new Set();
  const seriesLookups = timeseriesData.map(series => {
    const lookup = new Map();
    series.values.forEach(point => {
      const ts = normalizeTimestamp(point[0]);
      timestamps.add(ts);
      lookup.set(ts, parseFloat(point[1]));
    });
    return lookup;
  });

  // Numeric sort — lexicographic sort misorders timestamps of differing digit counts.
  const sortedTimestamps = Array.from(timestamps).sort((a, b) => a - b);

  const metrics = timeseriesData.map(series => {
    let name = 'Series';
    if (series.metric) {
      const labels = Object.entries(series.metric)
        .filter(([key]) => !key.startsWith('__'))
        .map(([key, value]) => `${key}="${value}"`)
        .join(', ');

      if (labels) {
        name = labels;
      } else if (series.metric.__name__) {
        name = series.metric.__name__;
      }
    }
    return { name };
  });

  const rows = sortedTimestamps.map((timestamp, idx) => {
    const row = {
      key: `ts-${idx}`,
      timestamp: new Date(timestamp).toLocaleString(),
      timestampRaw: timestamp,
    };

    seriesLookups.forEach((lookup, seriesIndex) => {
      row[`value_${seriesIndex}`] = lookup.get(timestamp) ?? null;
    });

    return row;
  });

  return { metrics, rows, uniqueTimestamps: sortedTimestamps };
}

export default React.memo(TimeSeriesTable, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.config?.unit === nextProps.config?.unit &&
    prevProps.config?.decimalPlaces === nextProps.config?.decimalPlaces &&
    prevProps.config?.useThousandSeparator === nextProps.config?.useThousandSeparator
  );
});
